/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, InputAdornment, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import DialogButton from '../../ui/utils/DialogButton';

const FILE_EXTENSION = '.json';

export const ExportDialog = ({ open, onCancel, onConfirm, fileName: initialFileName, hasConnections }) => {
	const [fileName, setFileName] = useState(initialFileName);

	const handleKeyPressed = (event) => {
		switch (event.key) {
			case 'Enter': {
				event.preventDefault();
				if (fileName) {
					onConfirm(event, `${fileName}${FILE_EXTENSION}`);
				}
				break;
			}
			case 'Escape': {
				event.preventDefault();
				onCancel();
				break;
			}
			default:
		}
	};

	return (
		<Dialog open={open} onKeyDown={handleKeyPressed}>
			<DialogTitle>
				<FormattedMessage id="Export.Dialog.Title" defaultMessage="Export" />
			</DialogTitle>
			<DialogContent
				style={{
					margin: '20px',
					width: '300px'
				}}
			>
				<TextField
					autoFocus
					margin="dense"
					label={<FormattedMessage id="Export.Dialog.FileName" defaultMessage="File Name" />}
					fullWidth
					onChange={(event) => setFileName(event.target.value)}
					value={fileName}
					InputProps={{
						endAdornment: <InputAdornment position="end">{FILE_EXTENSION}</InputAdornment>
					}}
				/>
			</DialogContent>
			<Divider />
			<DialogActions>
				<DialogButton margin onClick={onCancel}>
					<FormattedMessage id="Dialog.Cancel" defaultMessage="Cancel" />
				</DialogButton>
				<DialogButton
					margin
					disabled={!fileName}
					onClick={(event) =>
						onConfirm(event, { fileName: `${fileName}${FILE_EXTENSION}`, withConnections: false })
					}
				>
					<FormattedMessage id="Export.Dialog.Confirm" defaultMessage="Export" />
				</DialogButton>
				<DialogButton
					margin
					defaultButton
					disabled={!fileName || !hasConnections}
					onClick={(event) =>
						onConfirm(event, { fileName: `${fileName}${FILE_EXTENSION}`, withConnections: true })
					}
				>
					<FormattedMessage
						id="Export.Dialog.ConfirmWithConnections"
						defaultMessage="Export with Connections"
					/>
				</DialogButton>
			</DialogActions>
		</Dialog>
	);
};

ExportDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	fileName: PropTypes.string.isRequired
};
